import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { batch, useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'
import styled from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import type { CareHistoryProvider } from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'
import { ChatSideBarSelection } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/chatRightSideBar/ChatRightSideBar'
import { CoordinatedCareThreadList } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/coordinatedCareThreadList/CoordinatedCareThreadList'

import MessengerChat from './MessengerChat'
import { ProviderPairStatusBadge } from './ProviderPairStatusBadge'
import MessengerSideBar from './sideBar/MessengerSideBar'
import { mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { getClientFullName } from '../../../common/utils/utils'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../data/store'
import { useGuardianForSelectedClient } from '../../../hooks/useGuardianForSelectedClient'
import { useSelectedClientData } from '../../clients/clientDetails/data/hooks/useSelectedClientData'
import { setToastContent } from '../../data/ltToastAutoActions'
import { Template } from '../../documents/data/templatesApi'
import { ChatEntryPoint, InsertMessageOptions } from '../BaseChat'
import { buildCoordinatedCareThreadSections } from '../data/coordinatedCareThreads'
import { ESSENTIALS_ACCESS } from '../data/essentialsAccess'
import { usePatientConversationsData } from '../data/hooks/useConversationsData'
import { useEnsureConversationForThread } from '../data/hooks/useEnsureConversationForThread'
import { useEssentialsAccess } from '../data/hooks/useEssentialsAccess'
import { useInfiniteMessages } from '../data/hooks/useInfiniteMessages'
import { useMessengerThreads } from '../data/hooks/useMessengerThreads'
import { useEnsureProviderPairConversationForThread } from '../data/hooks/useProviderPairConversations'
import { useProviderPairMessagingAccess } from '../data/hooks/useProviderPairMessagingAccess'
import { useProviderPairMessengerThreads } from '../data/hooks/useProviderPairMessengerThreads'
import { useSelectMessengerThread } from '../data/hooks/useSelectMessengerThread'
import { getPendingInitialThreadId, getSelectedConversationId } from '../data/messagesSelectors'
import { clearSelectedConversation, setPendingInitialThreadId, setSelectedConversation } from '../data/messagesSlice'
import { getAiConversationMixpanelProperties } from '../data/mixpanelUtils'
import { isProviderPairThreadId } from '../data/providerPairUtils'
import { MessengerParticipant, MessengerThread, PARTICIPANT_CONFIG } from '../data/types'
import { hasRecipientMessages } from '../data/utils'
import useGetFilteredConversations from '../hooks/useGetFilteredConversations'

const Container = styled.View({
  flexDirection: 'row',
  flex: 1,
  overflow: 'hidden',
})

const ThreadListContainer = styled.View(({ theme }) => ({
  width: 240,
  borderRightWidth: 1,
  borderRightColor: theme.colors.borderDefault,
}))

const noop = () => {}

type BaseMessengerProps = {
  // Opens the pair thread with this provider, as a draft until its first send creates the conversation.
  initialProviderPairCounterpart?: CareHistoryProvider
  initialThreadId?: string
  initialMessage?: string
  onInitialMessageInserted?: () => void
  compact?: boolean
  compactDragHandle?: React.ReactNode
  testIDPrefix?: string
  onExpandMessages?: (thread: MessengerThread, message: string) => void
  onInitialProviderPairUnavailable?: () => void
  selectedClientId: string | null
  selectedEpisodeId?: string
  isComposing: boolean
  onSelectedClientIdChanged: (clientId: string | null) => void
  showEmptyView: boolean
  onIsComposingChanged: (isComposing: boolean) => void
  onOpenNewWindow?: () => void
  onClose?: () => void
  onOpenMessages?: () => void
  showClientThreadList?: boolean
  onOpenClientProfile: () => void
  onOpenAssignment: (assignmentId: string) => void
  onOpenNotes: (noteId: string) => void
  initialDetailPanelOpen: boolean
  isLoadingClient?: boolean
  entryPoint?: ChatEntryPoint
  messageToInsert: { message: string; opts?: InsertMessageOptions } | null
  insertMessage?: (message: string | null, opts?: InsertMessageOptions) => void
  shouldInsertDBL?: boolean
  onDBLInserted?: () => void
  insertDirectBookingLink?: () => void
  setMessageToInsert: (message: { message: string; opts?: InsertMessageOptions } | null) => void
  shouldInsertEssentialsLink?: boolean
  onEssentialsLinkInserted?: () => void
  insertEssentialsArticleChip?: () => void
  currentTemplateId?: string | null
}

const BaseMessenger: React.FC<BaseMessengerProps> = ({
  initialProviderPairCounterpart,
  initialThreadId,
  initialMessage,
  onInitialMessageInserted,
  compact = false,
  compactDragHandle,
  testIDPrefix,
  onExpandMessages,
  onInitialProviderPairUnavailable,
  selectedClientId,
  selectedEpisodeId,
  onSelectedClientIdChanged: setSelectedClientId,
  isComposing,
  showEmptyView,
  onIsComposingChanged: setIsComposing,
  onOpenNewWindow,
  onClose,
  onOpenMessages,
  onOpenClientProfile,
  onOpenAssignment,
  onOpenNotes,
  initialDetailPanelOpen,
  isLoadingClient = false,
  entryPoint,
  showClientThreadList = entryPoint === ChatEntryPoint.MESSAGES_TAB,
  messageToInsert,
  insertMessage = noop,
  shouldInsertDBL = false,
  onDBLInserted = noop,
  insertDirectBookingLink = noop,
  insertEssentialsArticleChip = noop,
  setMessageToInsert,
  shouldInsertEssentialsLink = false,
  onEssentialsLinkInserted = noop,
  currentTemplateId,
}) => {
  const { shouldShowMessengerDraftsUtility, isPreferredNameEnabled } = useFlags()
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const { formatMessage } = intl
  const previousSelectedThreadIdRef = useRef<string | null>(null)
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(initialDetailPanelOpen)
  const [documentToAttach, setDocumentToAttach] = useState<Template | null>(null)
  const [pendingSideBarSelection, setPendingSideBarSelection] = useState<ChatSideBarSelection | null>(null)

  const openSideBarItem = useCallback((selection: ChatSideBarSelection) => {
    setPendingSideBarSelection(selection)
    setIsDetailsPanelOpen(true)
  }, [])

  const attachDocumentToMessage = useCallback((doc: Template) => {
    setDocumentToAttach(doc)
  }, [])

  const clearPendingInsertions = useCallback(() => {
    insertMessage(null)
    onDBLInserted()
    onEssentialsLinkInserted()
    setDocumentToAttach(null)
  }, [insertMessage, onDBLInserted, onEssentialsLinkInserted])

  const { data: selectedClientDetails } = useSelectedClientData()
  const userId = useSelector(getAuthUserId)
  const isEssentialsDisabled = useEssentialsAccess() === ESSENTIALS_ACCESS.DISABLED

  const {
    threads: standardThreads,
    isLoading: isLoadingStandardThreads,
    threadsLoaded,
    aiThreadsLoaded,
  } = useMessengerThreads()
  const {
    threads: providerPairThreads,
    isLoading: isLoadingProviderPairThreads,
    threadsLoaded: providerPairThreadsLoaded,
    draftThreadId: initialPairThreadId,
    draftUnavailable: initialPairUnavailable,
  } = useProviderPairMessengerThreads(initialProviderPairCounterpart)
  const knownPairConversationIds = useMemo(
    () => new Set(providerPairThreads.flatMap((thread) => (thread.conversationId ? [thread.conversationId] : []))),
    [providerPairThreads],
  )
  const threads = useMemo(() => [...standardThreads, ...providerPairThreads], [providerPairThreads, standardThreads])
  const pendingInitialThreadId = useSelector(getPendingInitialThreadId)
  const selection = useSelectMessengerThread(
    threads,
    initialProviderPairCounterpart ? MessengerParticipant.PROVIDER_PAIR : MessengerParticipant.PATIENT,
    undefined,
    initialThreadId ?? initialPairThreadId,
  )
  const pendingThreadId = pendingInitialThreadId ?? null
  const pendingThread = pendingThreadId ? (threads.find((thread) => thread.id === pendingThreadId) ?? null) : null
  // Give an entry-point target precedence before effects run so the patient thread is never loaded
  // or marked read during the first render of an exact deep link.
  const selectedThreadId = pendingThreadId ?? selection.selectedThreadId
  const selectedThread = pendingThreadId ? pendingThread : selection.selectedThread
  const isProviderPairThread = selectedThread?.participant === MessengerParticipant.PROVIDER_PAIR
  // Keep saved drafts disabled during promotion so text typed after the first send stays mounted.
  const [createdPairConversationIds, setCreatedPairConversationIds] = useState<ReadonlySet<string>>(new Set())
  const isCreatedPairConversation = Boolean(
    selectedThread?.conversationId && createdPairConversationIds.has(selectedThread.conversationId),
  )
  // Listed threads already carry the backend's capabilities; no individual detail request is needed.
  const effectiveSelectedThread = useMemo(
    () => (selectedThread && isCreatedPairConversation ? { ...selectedThread, disableDrafts: true } : selectedThread),
    [isCreatedPairConversation, selectedThread],
  )
  const showThreadList =
    !compact &&
    canAccessProviderPairs &&
    showClientThreadList &&
    threads.length > 1 &&
    Boolean(selectedClientId) &&
    !showEmptyView &&
    !isComposing
  const threadSections = useMemo(
    () =>
      showThreadList
        ? buildCoordinatedCareThreadSections(
            threads.map((thread) => (thread.id === effectiveSelectedThread?.id ? effectiveSelectedThread : thread)),
            intl,
          )
        : [],
    [effectiveSelectedThread, intl, showThreadList, threads],
  )
  const selectedConversationId = selectedThread?.conversationId ?? null
  const reduxSelectedConversationId = useSelector(getSelectedConversationId)
  const isProviderPairSelection = isProviderPairThreadId(selectedThreadId)
  const reduxConversationIsProviderPair = Boolean(
    reduxSelectedConversationId && knownPairConversationIds.has(reduxSelectedConversationId),
  )
  const needsPairSelectionSync = isProviderPairSelection || reduxConversationIsProviderPair
  const canRenderSelectedThread =
    !((initialProviderPairCounterpart || initialThreadId) && !selectedThread) &&
    (!needsPairSelectionSync ||
      reduxSelectedConversationId === selectedConversationId ||
      // One render behind while the draft's chat (no conversation) catches up to the one it created.
      (isCreatedPairConversation && reduxSelectedConversationId === null))
  const isExactSelectionPending = Boolean(selectedThreadId && !selectedThread)
  const isLoadingSelectedThread = isProviderPairSelection ? isLoadingProviderPairThreads : isLoadingStandardThreads
  const { selectThread: setSelectedThread } = selection
  const selectThread = useCallback(
    (threadId: string) => {
      // BaseChat reads the conversation from Redux. Update it in the same render as a
      // non-pair tab change so the pair access guard does not unmount the chat in between.
      batch(() => {
        if (!isProviderPairThreadId(threadId)) {
          const conversationId = threads.find((thread) => thread.id === threadId)?.conversationId
          if (conversationId) {
            dispatch(setSelectedConversation(conversationId))
          } else {
            dispatch(clearSelectedConversation())
          }
        }
        setSelectedThread(threadId)
      })
    },
    [dispatch, setSelectedThread, threads],
  )

  const onThreadSelected = useCallback(
    (threadId: string) => {
      if (threadId !== selectedThreadId) {
        onInitialMessageInserted?.()
        selectThread(threadId)
      }
    },
    [onInitialMessageInserted, selectThread, selectedThreadId],
  )

  // Each hook ignores the other's threads; pair threads are created on first send, never on select.
  const ensureStandardConversation = useEnsureConversationForThread(selectedThread, threadsLoaded)
  const ensurePairConversation = useEnsureProviderPairConversationForThread(selectedThread)
  const ensureCreatedPairConversation = useCallback(async () => {
    const conversationId = await ensurePairConversation()
    if (conversationId) {
      setCreatedPairConversationIds((ids) => new Set(ids).add(conversationId))
    }
    return conversationId
  }, [ensurePairConversation])
  const ensureSelectedConversation = isProviderPairThread ? ensureCreatedPairConversation : ensureStandardConversation
  // History loading can remount the input after client/thread loading has settled.
  // Include it in MessengerChat's loading state so its focus effect runs for the mounted input.
  const { data: messagesData, isLoading: isLoadingMessages } = useInfiniteMessages()

  // Keep refetching the selected client's conversations on mount/arg change (parity with prior behavior).
  usePatientConversationsData(
    selectedClientDetails
      ? { providerId: userId, patientId: selectedClientDetails.id, types: CONVERSATION_TYPE.PROVIDER }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  )
  useEffect(() => {
    if (!initialDetailPanelOpen) {
      setIsDetailsPanelOpen(false)
    }
  }, [initialDetailPanelOpen])

  // Mirror the resolved thread's conversation id into Redux so BaseChat (which reads it there) follows tabs.
  // While null (guardian create window / failed create) the chat shows its empty state and send is disabled.
  useEffect(() => {
    if (selectedThreadId && !selectedThread) {
      return
    }
    if (reduxSelectedConversationId === selectedConversationId) {
      return
    }
    if (selectedConversationId) {
      dispatch(setSelectedConversation(selectedConversationId))
    } else {
      dispatch(clearSelectedConversation())
    }
  }, [dispatch, reduxSelectedConversationId, selectedConversationId, selectedThread, selectedThreadId])

  const { conversations: conversationsMapped } = useGetFilteredConversations([])
  const clientOptions = useMemo(
    () => conversationsMapped.map((client) => ({ id: client.clientId, name: client.clientName })),
    [conversationsMapped],
  )

  // Deep-link to the exact thread. The selection hook retains this target while its
  // patient-scoped conversation request is still in flight.
  useEffect(() => {
    if (pendingInitialThreadId) {
      selectThread(pendingInitialThreadId)
      dispatch(setPendingInitialThreadId(null))
    }
  }, [dispatch, pendingInitialThreadId, selectThread])

  // The opened provider already has a pair this provider can't open, so return the caller to its error.
  useEffect(() => {
    if (initialPairUnavailable && selectedThreadId === initialPairThreadId && onInitialProviderPairUnavailable) {
      clearPendingInsertions()
      dispatch(clearSelectedConversation())
      onInitialProviderPairUnavailable()
    }
  }, [
    clearPendingInsertions,
    dispatch,
    initialPairThreadId,
    initialPairUnavailable,
    onInitialProviderPairUnavailable,
    selectedThreadId,
  ])

  // Retain a missing target only while its backing thread source is unresolved. Once it settles,
  // recover to patient chat. This also prevents a removed guardian tab from leaving its previous
  // conversation selected indefinitely.
  useEffect(() => {
    const missingThreadHasSettled = selectedThreadId?.startsWith('ai:')
      ? aiThreadsLoaded
      : isProviderPairSelection
        ? providerPairThreadsLoaded
        : !isLoadingStandardThreads
    // The caller restores its previous conversation and closes on this error.
    // Do not overwrite that restoration with the normal patient fallback.
    if (initialPairUnavailable && onInitialProviderPairUnavailable && selectedThreadId === initialPairThreadId) return
    if (missingThreadHasSettled && selectedThreadId && !selectedThread) {
      selectThread(MessengerParticipant.PATIENT)
      dispatch(setPendingInitialThreadId(null))
    }
  }, [
    aiThreadsLoaded,
    dispatch,
    initialPairThreadId,
    initialPairUnavailable,
    onInitialProviderPairUnavailable,
    isLoadingStandardThreads,
    isProviderPairSelection,
    providerPairThreadsLoaded,
    selectThread,
    selectedThread,
    selectedThreadId,
  ])

  const selectedClient = useMemo(
    () => clientOptions.find((client) => client.id === selectedClientId),
    [clientOptions, selectedClientId],
  )

  const onOpenDetailsPanel = () => {
    track({
      event: mixpanelEvents.MESSAGE_CENTER_EXPAND_CLIENT_DETAILS,
      properties: getAiConversationMixpanelProperties(selectedThread?.conversationType),
    })
    setIsDetailsPanelOpen(true)
  }

  useEffect(() => {
    if (shouldShowMessengerDraftsUtility && isComposing) {
      setMessageToInsert({ message: '' })
    }
  }, [isComposing, setMessageToInsert, shouldShowMessengerDraftsUtility])

  const tabs = useMemo(
    () =>
      threads.map((thread) => ({
        id: thread.id,
        label: thread.tabLabel,
        badgeCount: thread.unreadCount,
        iconWithTooltip: thread.providerPairStatus ? (
          <ProviderPairStatusBadge
            status={thread.providerPairStatus}
            testID={`ProviderPairStatusBadge-tab-${thread.id}`}
          />
        ) : undefined,
      })),
    [threads],
  )

  const hasRecipientMessagesValue = useMemo(
    () =>
      hasRecipientMessages(
        { conversation_messages: messagesData, next_page_token: null },
        userId,
        selectedThread?.conversationType,
      ),
    [messagesData, selectedThread?.conversationType, userId],
  )

  const { guardian, isGuardianForMinor } = useGuardianForSelectedClient()
  const patientFullName = selectedClientDetails ? getClientFullName(selectedClientDetails, isPreferredNameEnabled) : ''
  let clientName = selectedClient?.name
  if (selectedClientDetails?.id === selectedClientId) {
    clientName = compact ? patientFullName : clientName || patientFullName
  }
  const canSendMessages = effectiveSelectedThread?.canSendMessages ?? !isExactSelectionPending
  const showProviderPairResponseTimeNotice =
    isProviderPairThread && effectiveSelectedThread?.recipientIsDirectAccess === true

  // Requests queued for one thread must not land in a different thread after access changes.
  useEffect(() => {
    const previousThreadId = previousSelectedThreadIdRef.current
    previousSelectedThreadIdRef.current = selectedThreadId
    const pairThreadChanged =
      previousThreadId !== selectedThreadId &&
      (isProviderPairThreadId(previousThreadId) || isProviderPairThreadId(selectedThreadId))
    if (pairThreadChanged || (isProviderPairThread && !canSendMessages)) {
      clearPendingInsertions()
    }
  }, [canSendMessages, clearPendingInsertions, isProviderPairThread, selectedThreadId])

  const showErrorToast = useCallback(
    (id: string, text: string) => dispatch(setToastContent({ id, text, toastType: 'error' })),
    [dispatch],
  )

  const onReadOnlyInsertAttempt = useCallback(() => {
    showErrorToast(
      'provider-pair-read-only-insert',
      formatMessage({
        defaultMessage: 'This thread is read-only. Choose another conversation to send a message.',
        description: 'Shown when a provider tries to add content to a read-only provider-pair thread',
      }),
    )
  }, [formatMessage, showErrorToast])

  return (
    <Container>
      {showThreadList && (
        <ThreadListContainer>
          <CoordinatedCareThreadList
            sections={threadSections}
            selectedId={selectedThreadId}
            onThreadPress={onThreadSelected}
          />
        </ThreadListContainer>
      )}
      <MessengerChat
        compactHeader={
          compact
            ? {
                threads: threads.filter(
                  (thread) =>
                    !PARTICIPANT_CONFIG[thread.participant].requiresExistingConversation ||
                    thread.conversationId ||
                    thread.id === initialPairThreadId,
                ),
                selectedThread,
                onThreadSelected,
                onExpand:
                  selectedThread && onExpandMessages
                    ? (message) => onExpandMessages(selectedThread, message)
                    : undefined,
                onClose,
                dragHandle: compactDragHandle,
                testIDPrefix,
              }
            : undefined
        }
        selectedEpisodeId={selectedEpisodeId}
        clientName={clientName}
        title={isProviderPairThread ? selectedThread.recipientName : undefined}
        isClientTitle={!isProviderPairThread}
        onOpenClientProfile={onOpenClientProfile}
        isLoading={isLoadingClient || isLoadingSelectedThread || isLoadingMessages || !canRenderSelectedThread}
        isVerifyingSelectedThread={!canRenderSelectedThread}
        showEmptyView={showEmptyView && !isLoadingClient && !isComposing}
        isDetailsPanelOpen={isDetailsPanelOpen}
        onOpenDetailsPanel={onOpenDetailsPanel}
        isComposing={isComposing}
        clients={clientOptions}
        onClientSelected={setSelectedClientId}
        onMessageSent={() => {
          setIsComposing(false)
          insertMessage(null)
        }}
        onOpenNewWindow={onOpenNewWindow}
        onClose={onClose}
        onOpenMessages={onOpenMessages}
        entryPoint={entryPoint}
        messageToInsert={
          initialMessage && selectedThreadId === initialThreadId ? { message: initialMessage } : messageToInsert
        }
        onMessageInserted={() => {
          if (initialMessage && selectedThreadId === initialThreadId) onInitialMessageInserted?.()
          else setMessageToInsert(null)
        }}
        onReadOnlyInsertAttempt={onReadOnlyInsertAttempt}
        shouldInsertDBL={shouldInsertDBL}
        onDBLInserted={onDBLInserted}
        shouldInsertEssentialsLink={shouldInsertEssentialsLink}
        onEssentialsLinkInserted={onEssentialsLinkInserted}
        currentTemplateId={currentTemplateId ?? undefined}
        tabs={showThreadList || compact ? [] : tabs}
        selectedTabId={selectedThreadId ?? ''}
        onTabSelected={onThreadSelected}
        ensureConversation={ensureSelectedConversation}
        canSendMessages={canSendMessages}
        showProviderPairResponseTimeNotice={showProviderPairResponseTimeNotice}
        providerPairAccess={isProviderPairThread ? effectiveSelectedThread?.providerPairAccess : undefined}
        providerPairStatus={isProviderPairThread ? effectiveSelectedThread?.providerPairStatus : undefined}
        showInputActions={effectiveSelectedThread?.showInputActions ?? !isExactSelectionPending}
        disableDrafts={effectiveSelectedThread?.disableDrafts ?? isExactSelectionPending}
        hasRecipientMessages={hasRecipientMessagesValue}
        recipientName={selectedThread?.recipientName}
        recipientId={selectedThread?.recipientId}
        patientFullName={isGuardianForMinor ? patientFullName : undefined}
        guardianName={isGuardianForMinor ? guardian.guardianName : undefined}
        onOpenDocumentLibrary={compact ? undefined : () => openSideBarItem(ChatSideBarSelection.DOCUMENT_LIBRARY)}
        onOpenSideBarItem={compact ? undefined : openSideBarItem}
        documentToAttach={documentToAttach}
        onDocumentAttached={() => setDocumentToAttach(null)}
      />
      {!compact && (
        <MessengerSideBar
          selectedClientId={selectedClientId}
          isDetailsPanelOpen={isDetailsPanelOpen}
          setIsDetailsPanelOpen={setIsDetailsPanelOpen}
          insertMessage={insertMessage}
          insertDirectBookingLink={insertDirectBookingLink}
          insertEssentialsArticleChip={insertEssentialsArticleChip}
          onOpenAssignment={onOpenAssignment}
          onOpenNotes={onOpenNotes}
          selectedConversationType={selectedThread?.conversationType ?? CONVERSATION_TYPE.PROVIDER}
          isEssentialsDisabled={isEssentialsDisabled}
          pendingSideBarSelection={pendingSideBarSelection}
          onPendingSideBarSelectionOpened={() => setPendingSideBarSelection(null)}
          onAttachDocument={attachDocumentToMessage}
        />
      )}
    </Container>
  )
}

export default BaseMessenger
